import React from 'react'
import { scorePassword, type PasswordStrength } from '../../lib/passwordValidation'

interface PasswordStrengthIndicatorProps {
  password: string
}

const SEGMENT_COUNT = 4

const LABEL_MAP: Record<PasswordStrength['label'], string> = {
  'too-short': 'Too short',
  'weak': 'Weak',
  'fair': 'Fair',
  'good': 'Good',
  'strong': 'Strong',
}

const COLOR_MAP: Record<PasswordStrength['label'], string> = {
  'too-short': 'bg-red-500',
  'weak': 'bg-red-500',
  'fair': 'bg-amber-400',
  'good': 'bg-green-400',
  'strong': 'bg-green-600',
}

const TEXT_COLOR_MAP: Record<PasswordStrength['label'], string> = {
  'too-short': 'text-red-600 dark:text-red-400',
  'weak': 'text-red-600 dark:text-red-400',
  'fair': 'text-amber-600 dark:text-amber-400',
  'good': 'text-green-600 dark:text-green-400',
  'strong': 'text-green-700 dark:text-green-400',
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
}) => {
  if (password.length === 0) {
    return null
  }

  const strength = scorePassword(password)
  const filledSegments = strength.score
  const barColor = COLOR_MAP[strength.label]
  const labelColor = TEXT_COLOR_MAP[strength.label]
  const labelText = LABEL_MAP[strength.label]

  return (
    <div className="mt-2 space-y-2" aria-live="polite" aria-label="Password strength">
      <div className="flex gap-1" role="group" aria-label="Password strength segments">
        {Array.from({ length: SEGMENT_COUNT }).map((_, index) => (
          <div
            key={index}
            className={[
              'h-1.5 flex-1 rounded-full transition-all duration-300',
              index < filledSegments ? barColor : 'bg-slate-200 dark:bg-slate-700',
            ].join(' ')}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className={['text-xs font-medium', labelColor].join(' ')}>
          {labelText}
        </span>
      </div>
      {strength.issues.length > 0 && (
        <ul className="space-y-0.5" aria-label="Password requirements">
          {strength.issues.map((issue) => (
            <li
              key={issue}
              className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1"
            >
              <span className="h-1 w-1 rounded-full bg-slate-400 dark:bg-slate-500 flex-shrink-0" />
              {issue}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
